/**
 * Specialties.jsx - Medical specialties showcase section
 * 
 * Displays the 4 main medical specialties offered:
 * - Orthopedic Surgery
 * - Oncology
 * - Dental Care
 * - Cosmetic Surgery
 * 
 * Each specialty card shows:
 * - Image
 * - Name and description
 * - Key treatments offered
 * - CTA to enquiry form
 * 
 * @author Curis Path Development Team
 */

import React from 'react';
import { specialties } from '../data/mock';
import { ArrowRight } from 'lucide-react';

const Specialties = () => {
  /**
   * Scrolls to the enquiry form section
   */
  const scrollToEnquiry = () => {
    const element = document.getElementById('enquiry');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="specialties" 
      className="section-padding" 
      style={{ background: 'var(--bg-section)' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">Our Medical Specialties</h2>
          <p 
            className="body-large mx-auto" 
            style={{ maxWidth: '700px', color: 'var(--text-secondary)' }}
          >
            Comprehensive healthcare solutions in Orthopedics, Oncology, Dental, 
            and Cosmetic Surgery with world-class expertise
          </p>
        </div>

        {/* Specialty Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {specialties.map((specialty) => (
            <div 
              key={specialty.id} 
              className={`voice-card ${specialty.colorClass}`}
            >
              {/* Specialty Image */}
              <div 
                className="mb-4 rounded-lg overflow-hidden" 
                style={{ height: '200px' }}
              >
                <img 
                  src={specialty.image} 
                  alt={specialty.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              
              {/* Specialty Name */}
              <h3 className="heading-3 mb-3">{specialty.name}</h3>
              
              {/* Description */}
              <p 
                className="body-small mb-4" 
                style={{ color: 'var(--text-secondary)' }}
              >
                {specialty.description}
              </p>

              {/* Key Treatments List */}
              <div className="mb-4">
                <div 
                  className="body-small font-semibold mb-2" 
                  style={{ color: 'var(--text-primary)' }}
                >
                  Key Treatments:
                </div>
                <div className="flex flex-wrap gap-2">
                  {specialty.treatments.map((treatment, index) => (
                    <span 
                      key={index}
                      className="caption px-3 py-1 rounded-full"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.5)',
                        border: '1px solid rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {treatment}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <button 
                className="btn-secondary w-full mt-auto flex items-center justify-center gap-2"
                onClick={scrollToEnquiry}
              >
                Learn More
                <ArrowRight size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Specialties;
